import React from "react";
import { Box, Chip, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";

// A labeled row of clickable pill/chip buttons, used inside FiltersDialog
// (src/components/filters/filtersDialog.jsx) for things like Catégorie,
// Remise or Tri. Single- or multi-select via the `multiple` prop:
// - multiple: `value` is an array of selected ids, clicking toggles one.
// - single (default): `value` is one id, clicking selects it (no deselect).
//
// `allowAll` (single-select only) prepends a synthetic "clear" chip with
// `_id: ""` and `allLabel` (defaults to filters.all, e.g. "Tout"), purely
// inside this component, so converted MenuItemFilter dropdowns keep the
// same "click back to no filter" affordance their built-in empty MenuItem
// used to provide — without mutating the shared option list constants
// (VisibilityList, StockList, UserRoleList, ...), which are reused
// elsewhere (add/edit forms) where an injected "All" value would be wrong.
const FilterChipGroup = React.memo(
  ({
    label,
    options,
    value,
    onChange,
    multiple = false,
    allowAll = false,
    allLabel,
  }) => {
    const { t } = useTranslation();

    const isSelected = (optionId) =>
      multiple
        ? (value || []).map(String).includes(String(optionId))
        : String(value ?? "") === String(optionId);

    const handleClick = (optionId) => {
      if (multiple) {
        const current = value || [];
        const exists = current.map(String).includes(String(optionId));
        const next = exists
          ? current.filter((id) => String(id) !== String(optionId))
          : [...current, optionId];
        onChange(next);
      } else {
        onChange(optionId);
      }
    };

    if (!options || options.length === 0) return null;

    const displayedOptions =
      allowAll && !multiple
        ? [{ _id: "", label: allLabel || t("filters.all") }, ...options]
        : options;

    return (
      <Box>
        {label && (
          <Typography
            variant="caption"
            sx={{
              display: "block",
              mb: 1,
              fontWeight: 700,
              letterSpacing: 0.6,
              textTransform: "uppercase",
              color: "text.secondary",
            }}
          >
            {label}
          </Typography>
        )}
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
          {displayedOptions.map((option) => {
            const selected = isSelected(option._id);
            return (
              <Chip
                key={String(option._id)}
                label={option.labelKey ? t(option.labelKey) : option.label}
                clickable
                color={selected ? "primary" : "default"}
                variant={selected ? "filled" : "outlined"}
                onClick={() => handleClick(option._id)}
                sx={{ borderRadius: "16px" }}
              />
            );
          })}
        </Box>
      </Box>
    );
  }
);

export default FilterChipGroup;
